import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Switch } from 'antd';
import { makeStyles } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import { Done } from '@material-ui/icons';
import Close from '@material-ui/icons/Close';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';
import { Project } from 'mdello-client-ts/dist/src/interfaces/common/project.interface';
import { CustomerProfile } from 'src/services/napi/customers/interfaces/fetchCustomersProfile.interface';
import camelCaseToTitleCase from 'src/helpers/camelCaseToTitleCase';
import valueType from 'src/helpers/valueType';
import { useGetCustomerProfile } from 'src/reactQuery/customers/queries/useGetCustomerProfile';
import { useUpdateCustomerProfile } from 'src/reactQuery/customers/mutations/useUpdateCustomerProfile';
import Addresses from './addresses/Addresses';
import ProfileEditModal from './ProfileEditModal';
import { getAvailableKeys } from './helpers';
import CButton from '../../../../../component/Button';
import { MultipleSelect } from '../../../../../component/Select';
import './index.css';
import Verifications from './verifications';
import { LocalStorageKey } from 'src/localStorage/enums/localStorageKey.enum';
import { getDataFromLocalStorage } from 'src/localStorage/getDataFromLocalStorage';
import { isCurrentProjectStimull } from 'src/util/projectCheckers';

const useStyles = makeStyles({
  actionBar: {
    border: '1px solid #CAD8FF',
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: '50px',
    padding: '35px 0px',
    width: '100%'
  },
  featuredOn: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    transform: 'translateY(-3px)'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500
  },
  switch: {
    marginLeft: '12px'
  }
});

interface ObjDataInterface {
  [key: string]: any;
}
interface ProfileProps {
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<any | undefined, unknown>>;
}
const Profile: FC<ProfileProps> = ({ refetch }) => {
  const classes = useStyles();
  const { customerId } = useParams<{ customerId: string }>();
  const { data: customerProfile, refetch: profileRefetch } = useGetCustomerProfile(customerId);
  const currentProject: Project | undefined = getDataFromLocalStorage(
    LocalStorageKey.CURRENT_PROJECT
  );

  const customerProfileMutation = useUpdateCustomerProfile({
    onSuccess: () => {
      profileRefetch();
      refetch();
    }
  });
  const [customerProfileDraft, setCustomerProfileDraft] = useState<CustomerProfile | undefined>(
    // @ts-ignore
    customerProfile
  );

  useEffect(() => {
    if (customerProfile?.userId) {
      // @ts-ignore
      setCustomerProfileDraft({ ...customerProfile });
    }
  }, [customerProfile]);

  const [verifyBadge, setVerifyBadge] = useState(false);
  const [selling, setSelling] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [featured, setFeatured] = useState([]);

  const names = [
    'Sellers / Home Page',
    'Carousel / Sellers Page',
    'Shops / Sellers Page',
    'Individuals / Sellers Page',
    'New Sellers / Sellers Page',
    'For Me / Sellers Page'
  ];

  const onUpdate = () => {
    customerProfileMutation.mutate({
      body: customerProfileDraft,
      profileId: customerId
    });

    setOpenModal(false);
  };

  const setDraftProfile = (data: CustomerProfile) => {
    setCustomerProfileDraft(data);
  };

  const updateVerify = (isVerified: boolean) => {
    if (!customerProfileDraft) return undefined;

    setVerifyBadge(isVerified);
    setCustomerProfileDraft({
      ...customerProfileDraft,
      verified: isVerified
    });
    const mutateVariables = {
      body: {
        ...customerProfileDraft,
        verified: isVerified
      },
      profileId: customerId
    };
    customerProfileMutation.mutate(mutateVariables);
  };

  const updateStatus = (isActive: boolean) => {
    if (!customerProfileDraft) return undefined;

    setSelling(isActive);
    setCustomerProfileDraft({
      ...customerProfileDraft,
      settings: {
        ...customerProfileDraft.settings,
        active: isActive
      }
    });
    const mutateVariables = {
      body: {
        ...customerProfileDraft,
        settings: {
          ...customerProfileDraft.settings,
          active: isActive
        }
      },
      profileId: customerId
    };
    customerProfileMutation.mutate(mutateVariables);
  };

  const onAddressesUpdate = (selectedAddress: any) => {
    if (selectedAddress && customerProfileDraft) {
      const tempAddresses = [...customerProfileDraft.settings.addresses];
      tempAddresses.push(selectedAddress);
      let tempProfile = { ...customerProfileDraft };
      tempProfile = {
        ...customerProfileDraft,
        settings: {
          ...customerProfileDraft.settings,
          addresses: tempAddresses
        }
      };
      customerProfileMutation.mutate({
        body: tempProfile,
        profileId: customerId
      });
    } else {
      customerProfileMutation.mutate({
        body: customerProfileDraft,
        profileId: customerId
      });
    }
  };

  const restrictedData: string[] = [
    '_id',
    'project',
    'settings',
    'userId',
    'profilePictureId',
    'profilePictureUrl',
    'coverPictureId',
    'coverPictureUrl',
    'createdAt',
    'updatedAt',
    'followed'
  ];
  const specialCaseData: string[] = ['categories'];

  function structureSpecialCaseData(key: string, data: any) {
    if (key === 'categories') {
      const categories = data.map((item: any) => item?.name);
      return { data: categories, title: 'Interests' };
    }
  }

  function compareFn(a: string, b: string, data: ObjDataInterface): number {
    const typeA = valueType(data[a]);
    const typeB = valueType(data[b]);

    if (typeA === 'object' && typeB !== 'object') return 1;
    if (typeA !== 'object' && typeB === 'object') return -1;
    if (typeA === 'object' && typeB === 'object') return 0;

    return 0;
  }

  function createElement(
    titleText: string | undefined,
    bodyText: string | React.ReactNode | undefined
  ) {
    return (
      <div key={`${titleText}-${bodyText}`}>
        {titleText && (
          <h4
            style={{
              color: '#9BA0AD',
              fontFamily: 'Poppins',
              fontSize: '12px',
              lineHeight: '100%',
              wordBreak: 'break-word'
            }}
          >
            {camelCaseToTitleCase(titleText)}
          </h4>
        )}
        {bodyText && (
          <p
            style={{
              fontFamily: 'Poppins',
              fontSize: '18px',
              fontWeight: 500,
              wordBreak: 'break-word'
            }}
          >
            {bodyText}
          </p>
        )}
      </div>
    );
  }

  function displayNestedElements(
    data: any,
    passedKey: string | undefined,
    nestedCount: number,
    isCurrentItemFirstObj: boolean
  ) {
    const dataType = valueType(data);

    if (!data && dataType !== 'number' && dataType !== 'boolean') {
      // possible values => undefined, null, NaN, ""
      return createElement(passedKey, '-');
    }

    if (dataType === 'string' || dataType === 'number') {
      return createElement(passedKey, String(data));
    }

    if (dataType === 'boolean') {
      return createElement(passedKey, data ? <Done /> : <Close />);
    }

    if (dataType === 'array') {
      if (data.length === 0) {
        return createElement(passedKey, '-');
      }

      let displayData = "Can't Display Data";
      const isArrayOfStrings = data.every((item: string) => typeof item === 'string');
      if (isArrayOfStrings) {
        displayData = data.join(', ');
      }

      return createElement(passedKey, displayData);
    }

    if (dataType === 'object') {
      const availableKeys = getAvailableKeys(data, restrictedData);

      if (availableKeys.length === 0) {
        return createElement(passedKey, '-');
      }

      const sortedAvailableKeys = availableKeys.sort((a: string, b: string): number =>
        compareFn(a, b, data)
      );
      const firstNoneEmptyObjIndex = sortedAvailableKeys.findIndex(
        (key) => valueType(data[key]) === 'object' && Object.keys(data[key]).length !== 0
      );

      const reactElement = sortedAvailableKeys.map((key, index) => {
        const objKey = key;
        const objValue = data[objKey];
        const isCurrentItemFirstObj = firstNoneEmptyObjIndex === index;

        const isKeySpecialCase = specialCaseData.includes(objKey);
        if (isKeySpecialCase) {
          const { data, title } = structureSpecialCaseData(objKey, objValue) || {};
          return displayNestedElements(data, title, nestedCount + 1, isCurrentItemFirstObj);
        }

        return displayNestedElements(objValue, objKey, nestedCount + 1, isCurrentItemFirstObj);
      });

      return (
        <div
          key={`customers-profile-section-${nestedCount}-${passedKey}`}
          className={`customers-profile-section-${nestedCount} ${
            isCurrentItemFirstObj ? 'first-element' : ''
          }`}
        >
          {passedKey && <h4 style={{ fontFamily: 'Poppins', fontSize: '28px' }}>{passedKey}</h4>}
          {reactElement}
        </div>
      );
    }
  }

  const CustomerProfileDetails = () => {
    if (!customerProfile) return null;

    return (
      <div className="customers-profile">
        {/* @ts-ignore */}
        <ImageList customerProfile={customerProfile} />
        {displayNestedElements(customerProfile, undefined, 1, true)}
      </div>
    );
  };

  const ImageList = ({ customerProfile }: { customerProfile: CustomerProfile }) => {
    const { profilePictureUrl, coverPictureUrl, fullName } = customerProfile;

    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: '20px',
          marginBottom: '30px',
          marginTop: '5px'
        }}
      >
        <Avatar
          alt={fullName || ''}
          src={profilePictureUrl}
          style={{ height: '100px', width: '100px' }}
        />
        {coverPictureUrl && (
          <img
            alt="cover"
            src={coverPictureUrl}
            style={{
              borderRadius: '10px',
              height: '70px',
              width: '120px'
            }}
          />
        )}
      </div>
    );
  };

  const ActionBar = () => {
    return customerProfile ? (
      <div className={classes.actionBar}>
        <div className={classes.span}>
          Verify Badge{' '}
          {customerProfileDraft && customerProfileDraft.verified ? (
            <strong>ON</strong>
          ) : (
            <strong>OFF</strong>
          )}
          <Switch
            className={classes.switch}
            defaultChecked={
              customerProfileDraft && customerProfileDraft.verified
                ? customerProfileDraft.verified
                : verifyBadge
            }
            onChange={(isVerified) => updateVerify(isVerified)}
          />
        </div>
        <div className={classes.span}>
          Selling{' '}
          {customerProfileDraft &&
          customerProfileDraft.settings &&
          customerProfileDraft.settings.active ? (
            <strong>ON</strong>
          ) : (
            <strong>OFF</strong>
          )}
          <Switch
            className={classes.switch}
            defaultChecked={
              customerProfileDraft &&
              customerProfileDraft.settings &&
              customerProfileDraft.settings.active
                ? customerProfileDraft.settings.active
                : selling
            }
            onChange={(isActive) => updateStatus(isActive)}
          />
        </div>
        <div className={classes.featuredOn}>
          Featured On
          <MultipleSelect
            currentValue={customerProfileDraft?.featuredOn ?? featured}
            handleChange={(value) => {
              if (!customerProfileDraft) return undefined;

              setFeatured(
                // On autofill we get a stringified value.
                typeof value === 'string' ? value.split(',') : value
              );
              setCustomerProfileDraft({
                ...customerProfileDraft,
                featuredOn: value
              });
              const mutateVariables = {
                body: {
                  ...customerProfileDraft,
                  featuredOn: value
                },
                profileId: customerId
              };
              customerProfileMutation.mutate(mutateVariables);
            }}
            selectOptions={names}
            styles={{ marginLeft: '12px', width: '180px' }}
            type="text"
          />
        </div>
      </div>
    ) : null;
  };

  return (
    <Layout
      style={{
        background: 'white',
        borderTop: '1px solid #9BA0AD',
        marginTop: '20px'
      }}
    >
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <div
          style={{
            fontFamily: 'Poppins',
            fontSize: '38px'
          }}
        >
          Profile
        </div>
        <CButton onClick={() => setOpenModal(true)} type="primary">
          Edit
        </CButton>
      </div>
      <div
        style={{
          padding: '0px 40px 0px 35px',
          textAlign: 'start'
        }}
      >
        <CustomerProfileDetails />
        <ActionBar />
      </div>
      {isCurrentProjectStimull && (
        <Verifications
          // @ts-ignore
          bankAccount={customerProfileDraft?.bankAccount || '-'}
          // @ts-ignore
          idPicture={customerProfileDraft?.metadata?.idPicture}
        />
      )}

      <ProfileEditModal
        customerProfileDraft={customerProfileDraft}
        onUpdate={onUpdate}
        open={openModal}
        setDraftProfile={setDraftProfile}
        setOpen={setOpenModal}
      />

      {!isCurrentProjectStimull ? (
        <Addresses
          addresses={customerProfile?.settings?.addresses ?? []}
          customerProfileDraft={customerProfileDraft}
          onUpdate={onAddressesUpdate}
          setDraftProfile={setDraftProfile}
        />
      ) : null}
    </Layout>
  );
};

export default Profile;
